import React, { FC, useState } from 'react';
import { CircularProgress, Grid, IconButton, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { Edit, Delete } from '@mui/icons-material';
import LoadingSpinner from '../components/LoadingSpinner';
import { formatDate, numberAsKr } from '../Formatters';
import { CANCEL_HISTORICAL_PRICE_UPDATE, GET_HISTORICAL_PRICE } from './historical.graphql';
import { Alert } from '@mui/material';
import { UpdateHistoricalPriceDialog } from './UpdateHistoricalPriceDialogue';
import { useMutation, useQuery } from '@apollo/client/react';

interface HistoricalPriceChange {
  id: string;
  timeOfChange: string;
  newPrice: number;
}

export interface HistoricalGuidePriceModel {
  id: string;
  price: number;
  pendingPriceChange: HistoricalPriceChange | null;
}

export const HistoricalPriceCard: FC = () => {
  const { loading, data } = useQuery<{ historical: HistoricalGuidePriceModel }>(GET_HISTORICAL_PRICE);

  return (
    <Stack>
      <Typography variant="h5" gutterBottom>
        Historisk dokument
      </Typography>
      {loading && <LoadingSpinner />}
      {data && <HistoricalTable historical={data.historical} />}
    </Stack>
  );
};

const HistoricalTable: FC<{ historical: HistoricalGuidePriceModel }> = ({ historical }) => {
  return (
    <Grid container>
      <Grid>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Navn</TableCell>
              <TableCell align="right">Pris</TableCell>
              <TableCell style={{ width: 355 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>Historisk dokument</TableCell>
              <TableCell align="right">
                <HistoricalPrice historical={historical} />
              </TableCell>
              <TableCell>
                <HistoricalPriceAction historical={historical} />
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Grid>
    </Grid>
  );
};

function HistoricalPrice({ historical }: { historical: HistoricalGuidePriceModel }) {
  const price = historical.price;
  if (!price) return null;
  const priceAsKr = numberAsKr(price);
  if (!historical.pendingPriceChange) return <span>{priceAsKr}</span>;
  return (
    <>
      <Typography sx={{textDecoration: 'line-through', display: 'inline'}} color="error" variant="body1">
        {priceAsKr}
      </Typography>
      <Typography color="primary" variant="body1" sx={{display: 'inline'}}>
        {numberAsKr(historical.pendingPriceChange.newPrice)}
      </Typography>
    </>
  );
}

const HistoricalPriceAction: FC<{ historical: HistoricalGuidePriceModel }> = ({ historical }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [cancelUpdate, { loading }] = useMutation<any, any>(CANCEL_HISTORICAL_PRICE_UPDATE);
  
  if (!historical.price) return null;

  const handleCancelUpdate = () => {
    cancelUpdate();
  };

  if (historical.pendingPriceChange) {
    return (
      <Stack direction="row" sx={{alignItems: 'center'}}>
        <div>
          <IconButton disabled={loading} title="Forkast endring" onClick={handleCancelUpdate} size="small">
            <Delete fontSize="small" />
          </IconButton>
        </div>
        <Alert
          sx={{
            display: 'inline-flex',
            verticalAlign: 'middle',
            pt: 0,
            pb: 0,
            '& .MuiAlert-message': {
              padding: 0,
            },
            '& .MuiAlert-icon': {
              padding: 0,
            },
          }}
          severity="info">
          Prisen vil bli oppdatert {formatDate(historical.pendingPriceChange.timeOfChange)}
        </Alert>
      </Stack>
    );
  } else {
    return (
      <Stack direction="row">
        <IconButton disabled={busy} title="Oppdater prisen" onClick={() => setOpen(true)} size="small">
          <Edit fontSize="small" />
        </IconButton>
        {open && <UpdateHistoricalPriceDialog historical={historical} onClose={() => setOpen(false)} setBusy={setBusy} />}
        {busy && <CircularProgress sx={{ verticalAlign: `middle` }} size={30} />}
      </Stack>
    );
  }
};